import React, { useState, useEffect } from "react";
import {
  Box, Button, TextField, Typography, useTheme, Card, CardContent,
  Grid, MenuItem, CircularProgress
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";
import { API_BASE_URL } from "../../config";

const Edit = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { table, id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const authToken = sessionStorage.getItem("authToken");
        const res = await fetch(`${API_BASE_URL}/admin/getData`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ authToken, table, id }),
        });
        const data = await res.json();
        if (data.success && data.data) {
          // Do not display existing hashed password in input
          const cleanData = { ...data.data };
          if (cleanData.password) cleanData.password = "";
          setFormData(cleanData);
        } else {
          toast.error(data.message || "Entrée introuvable");
        }
      } catch (err) {
        console.error("Erreur récupération données:", err);
        toast.error("Erreur serveur");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [table, id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const authToken = sessionStorage.getItem("authToken");
      // Clean empty password so we don't overwrite with empty string
      const payloadData = { ...formData };
      if (!payloadData.password) {
        delete payloadData.password;
      }

      const res = await fetch(`${API_BASE_URL}/admin/editData`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ authToken, table, id, data: payloadData }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Modifications enregistrées avec succès !");
        navigate(-1);
      } else {
        toast.error(data.message || "Erreur lors de la sauvegarde");
      }
    } catch (err) {
      console.error("Erreur enregistrement:", err);
      toast.error("Erreur serveur");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Êtes-vous certain de vouloir supprimer cette entrée définitivement ?")) return;
    try {
      const authToken = sessionStorage.getItem("authToken");
      const res = await fetch(`${API_BASE_URL}/admin/deleteData`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ authToken, table, id }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Entrée supprimée avec succès !");
        navigate(-1);
      } else {
        toast.error(data.message || "Erreur de suppression");
      }
    } catch (err) {
      console.error("Erreur suppression:", err);
      toast.error("Erreur serveur");
    }
  };

  if (loading) {
    return (
      <Box m="20px" display="flex" justifyContent="center" alignItems="center" height="60vh">
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb="20px">
        <Header
          title={`MODIFIER : ${table.toUpperCase()}`}
          subtitle={`Édition de l'enregistrement ID : ${id}`}
        />
        <Button
          onClick={() => navigate(-1)}
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          sx={{ color: colors.grey[100], borderColor: colors.grey[500] }}
        >
          Retour
        </Button>
      </Box>

      <Card sx={{ backgroundColor: colors.primary[400], maxWidth: "900px" }}>
        <CardContent sx={{ p: 4 }}>
          <form onSubmit={handleSave}>
            <Grid container spacing={3}>
              {/* UTILISATEURS (USERS) */}
              {table === "users" && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Nom d'utilisateur"
                      name="username"
                      value={formData.username || ""}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Numéro de Téléphone"
                      name="phone"
                      value={formData.phone || ""}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Adresse Email"
                      name="email"
                      type="email"
                      value={formData.email || ""}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Nouveau mot de passe (Laisser vide si inchangé)"
                      name="password"
                      type="password"
                      value={formData.password || ""}
                      onChange={handleChange}
                    />
                  </Grid>
                </>
              )}

              {/* CHAUFFEURS (DRIVERS) */}
              {table === "drivers" && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Nom"
                      name="name"
                      value={formData.name || ""}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Prénom"
                      name="fname"
                      value={formData.fname || ""}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Nom d'utilisateur"
                      name="username"
                      value={formData.username || ""}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Numéro de Téléphone"
                      name="phone"
                      value={formData.phone || ""}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Adresse Email"
                      name="email"
                      type="email"
                      value={formData.email || ""}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label="Catégorie Véhicule"
                      name="car"
                      value={formData.car || 1}
                      onChange={handleChange}
                    >
                      <MenuItem value={1}>Classic (Standard)</MenuItem>
                      <MenuItem value={2}>Confort</MenuItem>
                      <MenuItem value={3}>VIP Luxe</MenuItem>
                      <MenuItem value={4}>Woman (Chauffeur Femme)</MenuItem>
                      <MenuItem value={5}>Express (Livraison)</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Marque Véhicule"
                      name="car_brand"
                      value={formData.car_brand || ""}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Modèle Véhicule"
                      name="car_model"
                      value={formData.car_model || ""}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Immatriculation"
                      name="car_plate"
                      value={formData.car_plate || ""}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label="Statut Dossier / Vérification"
                      name="verified"
                      value={formData.verified !== undefined ? formData.verified : 0}
                      onChange={handleChange}
                    >
                      <MenuItem value={0}>En attente de validation</MenuItem>
                      <MenuItem value={1}>Dossier Validé</MenuItem>
                      <MenuItem value={2}>Dossier Rejeté</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Nouveau mot de passe (Optionnel)"
                      name="password"
                      type="password"
                      value={formData.password || ""}
                      onChange={handleChange}
                    />
                  </Grid>
                </>
              )}

              {/* ADMINS */}
              {table === "admins" && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Nom d'utilisateur"
                      name="username"
                      value={formData.username || ""}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Adresse Email"
                      name="email"
                      type="email"
                      value={formData.email || ""}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label="Rôle"
                      name="role"
                      value={formData.role || "admin"}
                      onChange={handleChange}
                    >
                      <MenuItem value="superadmin">Super Administrateur</MenuItem>
                      <MenuItem value="admin">Administrateur Standard</MenuItem>
                      <MenuItem value="support">Support Opérationnel</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Nouveau mot de passe (Optionnel)"
                      name="password"
                      type="password"
                      value={formData.password || ""}
                      onChange={handleChange}
                    />
                  </Grid>
                </>
              )}

              {/* BOUTONS D'ACTION */}
              <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between" mt="20px">
                  <Button
                    onClick={handleDelete}
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                  >
                    Supprimer Définitivement
                  </Button>

                  <Button
                    type="submit"
                    variant="contained"
                    disabled={saving}
                    startIcon={<SaveIcon />}
                    sx={{
                      backgroundColor: colors.greenAccent[600],
                      fontWeight: "bold",
                      padding: "10px 25px",
                      "&:hover": { backgroundColor: colors.greenAccent[700] },
                    }}
                  >
                    {saving ? "Sauvegarde..." : "Enregistrer les modifications"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Edit;
